// @ts-nocheck
/**
 * Keeps the One-Time Purchase hidden variant input aligned with the PDP variant picker.
 *
 * Do not rely on Liquid `closest.product.id` for which product the variant-picker controls:
 * combined listings / picker-driven product switches can make `closest.product` differ from
 * `variant-picker`’s live `dataset.productId` and from `variant:update`’s `data.productId`.
 * We always map using the event’s product id (and the picker’s dataset on first paint).
 */
import { ThemeEvents } from '@theme/events';

function variantMatches(variant, pdp) {
  return [1, 2, 3].every((i) => {
    const a = String(variant[`option${i}`] ?? '').trim();
    const b = String(pdp[`option${i}`] ?? pdp[`option_${i}`] ?? '').trim();
    return a === b;
  });
}

/**
 * @param {object | null | undefined} resource - Variant from variant:update / picker JSON
 * @param {Array<{ id: string | number; option1?: string; option2?: string; option3?: string; available?: boolean }>} otpVariants
 * @param {string} otpProductId
 * @param {string} viewedProductId - Product id for the variant picker / event (may differ from otp when PDP is another SKU)
 */
function resolveOtpVariant(resource, otpVariants, otpProductId, viewedProductId) {
  if (!resource || !otpVariants?.length) return null;

  if (String(otpProductId) === String(viewedProductId)) {
    const direct = otpVariants.find((v) => String(v.id) === String(resource.id));
    if (direct) return direct;
  }

  const match = otpVariants.find((v) => variantMatches(v, resource)) ?? otpVariants[0];
  return match ?? null;
}

function readPdpVariantFromSection(section) {
  const el = section?.querySelector('variant-picker script[type="application/json"]');
  if (!el?.textContent) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
}

function readPickerProductId(section) {
  return String(section?.querySelector('variant-picker')?.dataset?.productId ?? '');
}

function resolveVariantInput(host) {
  const formId = host.dataset.otpFormId;
  if (formId && typeof document !== 'undefined') {
    const byId = document.getElementById(formId)?.querySelector('input[name="id"]');
    if (byId instanceof HTMLInputElement) return byId;
  }
  const fallback = host.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
  return fallback instanceof HTMLInputElement ? fallback : null;
}

class OneTimePurchaseSync extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const section = this.closest('.shopify-section');
    const variantInput = resolveVariantInput(this);
    const variantsJson = this.querySelector('[data-otp-variants]');
    const otpProductId = this.dataset.otpProductId ?? '';
    const submitButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('button[type="submit"]'));

    let otpVariants = [];
    try {
      otpVariants = JSON.parse(variantsJson?.textContent || '[]');
    } catch {
      otpVariants = [];
    }

    if (!variantInput || !otpVariants.length || !section) return;

    const apply = (resource, viewedProductId) => {
      const resolved = resolveOtpVariant(resource, otpVariants, otpProductId, viewedProductId);
      if (!resolved) return;
      variantInput.value = String(resolved.id);
      if (submitButton) {
        submitButton.disabled = resolved.available === false;
      }
    };

    section.addEventListener(
      ThemeEvents.variantUpdate,
      (event) => {
        const resource = event.detail?.resource;
        const eventProductId = event.detail?.data?.productId;
        if (resource == null || eventProductId == null) return;
        apply(resource, String(eventProductId));
      },
      { signal }
    );

    const runInitial = () => {
      const initial = readPdpVariantFromSection(section);
      let viewedId = readPickerProductId(section);
      if (!viewedId && initial) {
        viewedId = String(otpProductId);
      }
      if (initial && viewedId) apply(initial, viewedId);
    };
    queueMicrotask(runInitial);
    requestAnimationFrame(() => requestAnimationFrame(runInitial));
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }
}

if (!customElements.get('one-time-purchase-sync')) {
  customElements.define('one-time-purchase-sync', OneTimePurchaseSync);
}
